import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getRedirectUri,
  getAppName,
  getAppConfig,
} from "../utils/redirectHelper";
import { useDomain } from "../contexts/DomainContext";
import CompanyLogo from "../components/CompanyLogo";
import AuthFooter from "../components/AuthFooter";
import "./Login.css";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { domainConfig } = useDomain();
  const [step, setStep] = useState(1); // Step 1: Username/Email, Step 2: Mobile No
  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  const redirectUri = getRedirectUri();
  const appName = getAppName();
  const appConfig = getAppConfig();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasRedirectUri =
      params.get("redirect_uri") || params.get("redirectUri");

    if (!hasRedirectUri) {
      console.warn("Direct access blocked - no redirect_uri parameter");
      navigate("/login");
      return;
    }
  }, [navigate]);

  const handleVerifyUsernameOrEmail = async (e) => {
    e.preventDefault();

    if (!usernameOrEmail) {
      toast.error("Please enter username or email address");
      return;
    }

    setLoading(true);

    try {
      const apiUrl =
        process.env.REACT_APP_SSO_API_URL || "https://localhost:7212/api/";
      
      // Extract domain from redirect URI or use window location
      let domain = window.location.hostname;
      if (redirectUri) {
        try {
          const url = new URL(redirectUri);
          domain = url.hostname;
        } catch (e) {
          console.warn("Could not parse redirect URI:", redirectUri);
        }
      }

      const response = await fetch(
        apiUrl + "Authorize/VerifyUsernameOfEmail",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            UserNameOrEmailID: usernameOrEmail,
            Url: domain,
          }),
        }
      );

      const data = await response.json();

      if (data.isSuccess && data.data && data.data.length > 0) {
        const user = data.data[0];
        setUserInfo(user);
        setCountryCode(user.countryCode || "+91");
        setStep(2);
        toast.success("User verified. Please enter your mobile number.");
      } else {
        toast.error(
          data.message ||
            "Username or email not found. Please try again."
        );
      }
    } catch (error) {
      console.error("Verify username error:", error);
      toast.error(
        error.message || "Error verifying username. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!mobileNo) {
      toast.error("Please enter your mobile number");
      return;
    }

    const digitsOnly = mobileNo.replace(/\D/g, "");
    if (digitsOnly.length < 7 || digitsOnly.length > 15) {
      toast.error("Please enter a valid mobile number");
      return;
    }

    setLoading(true);

    try {
      const apiUrl =
        process.env.REACT_APP_SSO_API_URL || "https://localhost:7212/api/";

      const response = await fetch(apiUrl + "Authorize/ForgotPassword", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: usernameOrEmail,
          MobileNo: mobileNo,
        }),
      });

      const data = await response.json();

      if (data.isSuccess) {
        setSubmitted(true);
        setSuccessMessage(
          data.message ||
            "Password has been sent to your registered mobile number."
        );
        toast.success(
          data.message ||
            "Password has been sent successfully."
        );
        setTimeout(() => {
          navigate("/login" + window.location.search);
        }, 3000);
      } else {
        toast.error(
          data.message ||
            "Unable to process your request. Please try again later."
        );
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      toast.error(
        error.message || "An error occurred. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    setStep(1);
    setMobileNo("");
    setUserInfo(null);
  };

  return (
    <div
      className="sso-login-container"
      style={{
        backgroundImage: domainConfig?.backgroundImage
          ? `url(${domainConfig.backgroundImage})`
          : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="login-card">
        {!submitted ? (
          <>
            <div className="login-header">
              <CompanyLogo 
                appConfig={appConfig} 
                domainConfig={domainConfig} 
                appName={appName}
              />
              <p className="subtitle">Reset Your Password</p>
            </div>

            {step === 1 ? (
              <form onSubmit={handleVerifyUsernameOrEmail} className="login-form">
                <p style={{ color: "#666", marginBottom: "20px" }}>
                  Enter your username or email address to verify your account.
                </p>

                <div className="form-group">
                  <label htmlFor="usernameOrEmail">Username or Email</label>
                  <input
                    type="text"
                    id="usernameOrEmail"
                    name="usernameOrEmail"
                    className="form-control"
                    placeholder="Enter your username or email"
                    value={usernameOrEmail}
                    onChange={(e) => setUsernameOrEmail(e.target.value)}
                    autoComplete="off"
                    autoFocus
                  />
                </div>

                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? "Verifying..." : "Verify Account"}
                </button>
              </form>
            ) : (
              <form onSubmit={handleForgotPassword} className="login-form">
                <p style={{ color: "#666", marginBottom: "20px" }}>
                  Account verified. Please enter your registered mobile number to
                  receive your password.
                </p>

                <div className="form-group">
                  <label htmlFor="usernameOrEmail">Username or Email</label>
                  <input
                    type="text"
                    id="usernameOrEmail"
                    className="form-control"
                    value={usernameOrEmail}
                    disabled
                    style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="mobileNo">Mobile Number</label>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <input
                      type="text"
                      className="form-control"
                      value={countryCode}
                      disabled
                      style={{
                        width: "80px",
                        backgroundColor: "#f5f5f5",
                        cursor: "not-allowed",
                      }}
                    />
                    <input
                      type="text"
                      id="mobileNo"
                      name="mobileNo"
                      className="form-control"
                      placeholder="Enter your mobile number"
                      value={mobileNo}
                      onChange={(e) => setMobileNo(e.target.value)}
                      autoComplete="off"
                      autoFocus
                    />
                  </div>
                </div>

                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? "Sending..." : "Send Password"}
                </button>
              </form>
            )}

            <div className="login-footer">
              {step === 2 && (
                <p className="help-text">
                  <button
                    type="button"
                    className="link-button"
                    onClick={handleGoBack}
                  >
                    ← Back
                  </button>
                </p>
              )}
              <p className="help-text">
                Remember your password?{" "}
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    const currentSearch = window.location.search;
                    navigate("/login" + currentSearch);
                  }}
                >
                  Sign In
                </button>
              </p>
            </div>
            <AuthFooter 
              domainConfig={domainConfig}
            />
          </>
        ) : (
          <>
            <div className="login-header">
              <div style={{ fontSize: "48px", marginBottom: "20px" }}>✅</div>
              <p className="subtitle">Password Sent Successfully</p>
            </div>

            <div
              style={{
                textAlign: "center",
                padding: "20px",
                color: "#666",
              }}
            >
              <p style={{ marginBottom: "15px" }}>
                {successMessage}
              </p>
              <p style={{ marginTop: "15px", fontSize: "14px" }}>
                Please check your registered mobile number for your password.
              </p>
              <p style={{ marginTop: "15px", fontSize: "14px", color: "#999" }}>
                Redirecting to login page...
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
